import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, MessageCircle } from 'react-feather';
import PropTypes from 'prop-types';

/**
 * TestimonialCarousel Component
 * Auto-rotating carousel of testimonials with accessibility features
 *
 * Features:
 * - Auto-rotation every 7 seconds
 * - Pause on hover/focus
 * - Keyboard navigation (arrow keys)
 * - Navigation dots
 * - WCAG 2.1 AA compliant
 */
const TestimonialCarousel = ({ testimonials }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    // Auto-rotation logic
    useEffect(
        () => {
            if (isPaused || testimonials.length <= 1) return;

            const interval = setInterval(() => {
                setCurrentIndex(prevIndex => (prevIndex + 1) % testimonials.length);
            }, 7000);

            return () => clearInterval(interval);
        },
        [isPaused, testimonials.length]
    );

    // Navigation functions
    const goToNext = useCallback(
        () => {
            setCurrentIndex(prevIndex => (prevIndex + 1) % testimonials.length);
        },
        [testimonials.length]
    );

    const goToPrevious = useCallback(
        () => {
            setCurrentIndex(prevIndex =>
                prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
            );
        },
        [testimonials.length]
    );

    const goToSlide = useCallback(index => {
        setCurrentIndex(index);
    }, []);

    // Keyboard navigation - only when carousel area has focus, not when typing in inputs
    useEffect(
        () => {
            const handleKeyDown = e => {
                // Don't interfere with form input navigation
                const activeElement = document.activeElement;
                const isFormElement =
                    activeElement &&
                    (activeElement.tagName === 'INPUT' ||
                        activeElement.tagName === 'TEXTAREA' ||
                        activeElement.tagName === 'SELECT');

                if (isFormElement) {
                    return; // Let the user navigate within form fields
                }

                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    goToPrevious();
                } else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    goToNext();
                }
            };

            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        },
        [goToNext, goToPrevious]
    );

    if (!testimonials || testimonials.length === 0) {
        return null;
    }

    const currentTestimonial = testimonials[currentIndex];

    return (
        <div
            className="testimonial-carousel"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
            role="region"
            aria-label="Customer testimonials"
            aria-live="polite"
        >
            {/* Carousel Content */}
            <div className="testimonial-carousel__content">
                {/* Initials Circle */}
                <div className="testimonial-carousel__initials" aria-hidden="true">
                    {currentTestimonial.initials}
                </div>

                {/* Quote */}
                <div className="testimonial-carousel__quote">
                    <MessageCircle
                        size={16}
                        className="testimonial-carousel__quote-icon"
                        aria-hidden="true"
                    />
                    {currentTestimonial.quote}
                </div>

                {/* Author Info */}
                <div className="testimonial-carousel__author">{currentTestimonial.authorName}</div>
                <div className="testimonial-carousel__title">
                    {currentTestimonial.authorTitle && `${currentTestimonial.authorTitle}, `}
                    {currentTestimonial.authorCompany}
                </div>
            </div>

            {/* Navigation Controls */}
            {testimonials.length > 1 && (
                <>
                    {/* Previous Button */}
                    <button
                        className="testimonial-carousel__nav testimonial-carousel__nav--prev"
                        onClick={goToPrevious}
                        aria-label="Previous testimonial"
                        type="button"
                    >
                        <ChevronLeft size={20} />
                    </button>

                    {/* Next Button */}
                    <button
                        className="testimonial-carousel__nav testimonial-carousel__nav--next"
                        onClick={goToNext}
                        aria-label="Next testimonial"
                        type="button"
                    >
                        <ChevronRight size={20} />
                    </button>

                    {/* Navigation Dots */}
                    <div
                        className="testimonial-carousel__dots"
                        role="tablist"
                        aria-label="Testimonial navigation"
                    >
                        {testimonials.map((testimonial, index) => (
                            <button
                                key={testimonial.id}
                                className={`testimonial-carousel__dot ${
                                    index === currentIndex
                                        ? 'testimonial-carousel__dot--active'
                                        : ''
                                }`}
                                onClick={() => goToSlide(index)}
                                aria-label={`Go to testimonial ${index + 1} of ${
                                    testimonials.length
                                }`}
                                aria-selected={index === currentIndex}
                                role="tab"
                                type="button"
                            />
                        ))}
                    </div>
                </>
            )}

            {/* Screen Reader Announcements */}
            <div className="sr-only" aria-live="polite" aria-atomic="true">
                Showing testimonial {currentIndex + 1} of {testimonials.length}
                {isPaused && ' - Carousel paused'}
            </div>
        </div>
    );
};

TestimonialCarousel.propTypes = {
    testimonials: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            quote: PropTypes.string.isRequired,
            authorName: PropTypes.string.isRequired,
            authorTitle: PropTypes.string,
            authorCompany: PropTypes.string,
            initials: PropTypes.string.isRequired,
        })
    ).isRequired,
};

export default TestimonialCarousel;
